/**
 * AI Assists Settings Page
 *
 * Deep-link hub for inline assists, on-demand narrators, and embedded advisors.
 * Primary surface is Settings → Agents (same catalog embedded there).
 */

import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Info, ChevronLeft } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useAiAssistSettingsStore } from '../../../stores/prioritization/aiAssistSettingsStore';
import { isAiCapabilityEnabled } from '../../../lib/ai/aiAssistSettings';
import { assistCapabilitiesFor } from '../../../lib/ai/useAiCapability';
import { useElevationAccess } from '../../../lib/elevationAccess';
import { AiAssistsCatalog } from './AiAssistsCatalog';

export default function AiAssistsPage() {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const slice = useAiAssistSettingsStore((s) =>
    workspaceId ? s.byWorkspace[workspaceId] : undefined,
  );
  const saveEnabled = useAiAssistSettingsStore((s) => s.saveEnabled);
  const savingId = useAiAssistSettingsStore((s) => s.savingCapabilityId);

  const handleToggle = useCallback(
    (capabilityId: string, enabled: boolean) => {
      if (!workspaceId) return;
      void saveEnabled(workspaceId, capabilityId, enabled, userId ?? undefined);
    },
    [workspaceId, userId, saveEnabled],
  );

  // Counts what the catalog below lists: an assist with an Elevation level
  // only for people at that level.
  const assists = assistCapabilitiesFor(useElevationAccess());
  const enabledCount = assists.filter((c) =>
    isAiCapabilityEnabled(c.id, workspaceId),
  ).length;

  return (
    <div className="max-w-3xl space-y-6">
      <Link
        to="/prioritization/settings/agents"
        className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] no-underline"
      >
        <ChevronLeft className="h-3.5 w-3.5" /> Back to Agents
      </Link>

      <PageHeader
        title="AI Assists"
        description="Inline assists, narrators, and advisors for this workspace. Changes apply to all users. Reached from Settings → Agents."
      />

      <div className="p-4 border border-[var(--slate)] bg-[var(--slate-dim)]/60 flex gap-3">
        <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[var(--text-ter)]">
          <p className="m-0 text-[var(--text-ter)]">
            {enabledCount} of {assists.length} assists enabled.
            {slice?.isLoading ? ' Loading workspace settings…' : ' Settings are saved to the workspace.'}
          </p>
        </div>
      </div>

      <AiAssistsCatalog
        workspaceId={workspaceId}
        savingCapabilityId={savingId}
        onToggle={handleToggle}
      />
    </div>
  );
}
